import React, { useState, useEffect } from 'react'
import PublicAppHeader from '../PublicAppHeader'
import { getAllCoursesCategoryItemsForPublic } from 'src/consumer/services/featureServices'
import { LoadingSpinner } from '../Loaders'
import { APP_CONSTANTS } from 'src/consumer/constants'
import { SEOComponent } from 'src/consumer/helpers/utilFunctions'
import {
  buildBreadcrumbs,
  buildPodcastSeriesJsonLd,
} from 'src/consumer/helpers/seoHelpers'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import {
  PublicListingComponentVarOne,
  PublicListingFilterDrawer,
  calculateNumberOfFilters,
} from '../ListingPageComponentsAndHelpers/ListingPageComponents'
import { useParams } from 'react-router-dom'
import { getListItemsFromCoursesList } from '../ListingPageComponentsAndHelpers/ListingPageHelpers'
import { useDispatch, useSelector } from 'react-redux'
import { getDataWithParams } from 'src/consumer/services/profileService'
import { ClubbedAuthModal } from '../SignIn/ClubbedAuthComponents'
import {
  selectCategoriesList,
  selectTagsList,
  setCategoriesList,
  setTagsList,
} from 'src/consumer/reducers/genericData/genericDataReducer'

export default function CourseCategoryListingPage({ prechosenCategory }) {
  const dispatch = useDispatch()
  const { categorySlug } = useParams()
  const finalCategorySlug = categorySlug || prechosenCategory
  const urlParams = new URLSearchParams(window.location.search) // respected params: duration
  const durationFilter = urlParams?.has('duration') ? urlParams.get('duration') : null

  const categoriesListFromStore = useSelector(selectCategoriesList)
  const tagsListFromStore = useSelector(selectTagsList)

  const [pageLoading, setPageLoading] = useState(false)
  const [coursesList, setCoursesList] = useState()

  const [pageNumber, setPageNumber] = useState(1)
  const [hasNextPage, setHasNextPage] = useState(true)

  const [showClubbedAuthModal, setShowClubbedAuthModal] = useState(false)
  const [typeOfClubbedAuthModal, setTypeOfClubbedAuthModal] = useState('login')

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState({
    search: '', // search string
    duration: null, // short,medium,long
    courseType: null, // single day multi
    type: null, // audio, video
    tags: [], // genres
    languages: [], // languages
  })

  async function pageSetup() {
    setPageLoading(true)
    const resp = await getAllCoursesCategoryItemsForPublic(
      1,
      finalCategorySlug,
      getMinDuration(selectedFilters?.duration),
      getMaxDuration(selectedFilters?.duration),
      selectedFilters?.courseType,
      selectedFilters?.type,
      selectedFilters?.tags?.join(','),
    )
    if (resp?.status === 200) {
      setCoursesList(resp?.data)
    }
    if (!Boolean(categoriesListFromStore)) {
      const response = await getDataWithParams('/api/external/app-listable/list', {
        feature: 'categories',
      })
      if (response?.status === 200 && Array.isArray(response?.data)) {
        dispatch(setCategoriesList(response?.data))
      }
    }
    if (!Boolean(tagsListFromStore)) {
      const responseTag = await getDataWithParams('/api/external/app-listable/list', {
        feature: 'tag',
      })
      if (responseTag?.status === 200 && Array.isArray(responseTag?.data)) {
        dispatch(setTagsList(responseTag?.data))
      }
    }
    setPageLoading(false)
  }

  async function getNextCoursesPage() {
    const nextPageNumber = pageNumber + 1
    const resp = await getAllCoursesCategoryItemsForPublic(
      nextPageNumber,
      finalCategorySlug,
      getMinDuration(selectedFilters?.duration),
      getMaxDuration(selectedFilters?.duration),
      selectedFilters?.courseType,
      selectedFilters?.type,
      selectedFilters?.tags?.join(','),
    )
    if (resp?.status === 200) {
      if (resp?.data && resp?.data?.length > 0) {
        setPageNumber(nextPageNumber)
        setCoursesList((prev) => [...prev, ...resp?.data])
      } else {
        // If there's no more data to load, set hasNextPage to false
        setHasNextPage(false)
      }
    } else {
      setHasNextPage(false)
    }
  }

  const [sentryRef] = useInfiniteScroll({
    hasNextPage,
    onLoadMore: getNextCoursesPage,
    rootMargin: '0px 0px 20px 0px',
  })

  useEffect(() => {
    pageSetup()
  }, [])

  const categoryTitleForSeoComponent =
    finalCategorySlug === 'podcast'
      ? 'AUMHUM Talks'
      : categoriesListFromStore?.find((item) => item?.assessmentId === finalCategorySlug)?.title ||
        'Courses'

  const categoryDescriptionForSeoComponent =
    finalCategorySlug === 'podcast'
      ? 'AUMHUM Talks is a podcast that brings together mental health and various experts to explore topics on life, health, and beyond. Designed to inspire, these conversations aim to help you reset, heal, and grow.'
      : `Explore ${categoryTitleForSeoComponent}`

  const categoryImageForSeoComponent =
    finalCategorySlug === 'podcast'
      ? APP_CONSTANTS.PODCASTS_IMAGE_LINK
      : APP_CONSTANTS.APP_LOGO_LINK

  return (
    <>
      <SEOComponent
        title={
          finalCategorySlug === 'podcast'
            ? 'The AUMHUM Podcast — candid conversations on mental wellness'
            : `${categoryTitleForSeoComponent} | AUMHUM`
        }
        description={
          finalCategorySlug === 'podcast'
            ? "Clinicians, researchers, and people with lived experience in long-form conversations about mental health and the care behind it. Listen on YouTube or through AUMHUM."
            : categoryDescriptionForSeoComponent
        }
        image={categoryImageForSeoComponent}
        pathname={
          finalCategorySlug === 'podcast'
            ? '/podcast'
            : `/c/${finalCategorySlug || ''}`
        }
        keywordsArr={
          finalCategorySlug === 'podcast'
            ? [
                'AUMHUM podcast',
                'mental health podcast',
                'psychiatry podcast',
                'psychology podcast',
                'mental wellness conversations',
              ]
            : ['mental wellness', 'meditation', 'guided practice', 'self-care']
        }
        jsonLd={[
          buildBreadcrumbs([
            { name: 'Home', path: '/' },
            finalCategorySlug === 'podcast'
              ? { name: 'Podcast', path: '/podcast' }
              : { name: categoryTitleForSeoComponent, path: `/c/${finalCategorySlug || ''}` },
          ]),
          finalCategorySlug === 'podcast'
            ? buildPodcastSeriesJsonLd({
                name: 'The AUMHUM Podcast',
                description:
                  'Candid conversations on mental wellness — clinicians, researchers, and people with lived experience in long-form discussions.',
                pathname: '/podcast',
              })
            : null,
        ]}
      />
      <ClubbedAuthModal
        open={showClubbedAuthModal}
        onClose={() => setShowClubbedAuthModal(false)}
        loginVariant={typeOfClubbedAuthModal === 'login'}
      />
      <PublicAppHeader
        config={{ showLinks: false }}
        loginFunction={() => {
          setTypeOfClubbedAuthModal('login')
          setShowClubbedAuthModal(true)
        }}
        registerFunction={() => {
          setTypeOfClubbedAuthModal('signup')
          setShowClubbedAuthModal(true)
        }}
      />
      {pageLoading ? (
        <LoadingSpinner bgColor="#F5F5F599" dark />
      ) : (
        <>
          <PublicListingComponentVarOne
            heading={`Explore ${categoryTitleForSeoComponent}`}
            description={
              finalCategorySlug === 'podcast'
                ? 'AUMHUM Talks is a podcast that brings together mental health and various experts to explore topics on life, health, and beyond. Designed to inspire, these conversations aim to help you reset, heal, and grow.'
                : `Explore ${categoryTitleForSeoComponent} curated by experts`
            }
            actionText="Explore"
            showFilterOption={true}
            areFiltersSelected={calculateNumberOfFilters(selectedFilters)}
            filterOptionClickFunction={() => setFilterDrawerOpen(true)}
            listItemsArray={getListItemsFromCoursesList(coursesList, finalCategorySlug)}
            sentryRef={sentryRef}
            hasNextPage={hasNextPage}
          />
        </>
      )}
      <PublicListingFilterDrawer
        isOpen={filterDrawerOpen}
        toggleDrawer={() => setFilterDrawerOpen(false)}
        tagsData={tagsListFromStore}
        filters={{
          duration: true,
          type: true,
          tags: true,
          courseType: true,
        }}
        selectedFilters={selectedFilters}
        changeSelectedFiltersFunction={(data) => setSelectedFilters(data)}
        applyFiltersFunction={() => {
          setPageNumber(1)
          setHasNextPage(true)
          setCoursesList()
          pageSetup()
        }}
        actionButtonText={`Show ${categoryTitleForSeoComponent}`}
      />
    </>
  )
}

function getMinDuration(durationText) {
  // short,medium,long
  const minDuration = { short: null, medium: 5 * 60 * 1000, long: 15 * 60 * 1000 }
  return minDuration[durationText]
}

function getMaxDuration(durationText) {
  // short,medium,long
  const maxDuration = { short: 5 * 60 * 1000, medium: 15 * 60 * 1000, long: null }
  return maxDuration[durationText]
}
